import React from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { CheckCircle2, AlertCircle, User, Users } from 'lucide-react';
import SignatureCanvas from 'react-signature-canvas';

/**
 * Builds the waiver form sections array.
 * ctx provides all state and handlers owned by WaiverPage.
 */
export const buildWaiverSections = (ctx) => {
  const {
    formData,
    handleChange,
    fieldErrors,
    initials,
    handleInitialChange,
    signingFor,
    setSigningFor,
    mediaConsent,
    setMediaConsent,
    signerIsParent,
    setSignerIsParent,
    signerInfo,
    setSignerInfo,
    sigPadRef,
    clearSignature
  } = ctx;

  return [
    // Section 0: Signing For Selection
    {
      title: 'Who are you signing for?',
      content: (
        <div className="signing-for-selection">
          <p className="selection-description">
            Please select who will be participating in CAL FIT activities:
          </p>
          <div className="signing-options">
            <button
              type="button"
              className={`signing-option ${signingFor === 'myself' ? 'selected' : ''}`}
              onClick={() => setSigningFor('myself')}
              data-testid="signing-myself-btn"
            >
              <User size={32} />
              <span className="option-title">Myself</span>
              <span className="option-desc">I am an adult (18+) signing for myself</span>
            </button>
            <button
              type="button"
              className={`signing-option ${signingFor === 'minor' ? 'selected' : ''}`}
              onClick={() => setSigningFor('minor')}
              data-testid="signing-minor-btn"
            >
              <Users size={32} />
              <span className="option-title">A Minor Child</span>
              <span className="option-desc">I am a parent/guardian signing for my child</span>
            </button>
          </div>
        </div>
      )
    },
    // Section 1: Participant Information
    {
      title: signingFor === 'myself' ? 'Participant Information' : 'Minor & Parent/Guardian Information',
      content: signingFor === 'myself' ? (
        <div className="form-section">
          <div className="form-row">
            <div className="form-group">
              <Label htmlFor="firstName">First Name *</Label>
              <Input
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                className={fieldErrors.firstName ? 'input-error' : ''}
                required
                data-testid="waiver-firstname"
              />
              {fieldErrors.firstName && <span className="field-error">{fieldErrors.firstName}</span>}
            </div>
            <div className="form-group">
              <Label htmlFor="lastName">Last Name *</Label>
              <Input
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                className={fieldErrors.lastName ? 'input-error' : ''}
                required
                data-testid="waiver-lastname"
              />
              {fieldErrors.lastName && <span className="field-error">{fieldErrors.lastName}</span>}
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <Label htmlFor="dateOfBirth">Date of Birth *</Label>
              <Input
                id="dateOfBirth"
                name="dateOfBirth"
                type="date"
                value={formData.dateOfBirth}
                onChange={handleChange}
                className={fieldErrors.dateOfBirth ? 'input-error' : ''}
                required
                data-testid="waiver-dob"
              />
              {fieldErrors.dateOfBirth && <span className="field-error">{fieldErrors.dateOfBirth}</span>}
            </div>
            <div className="form-group">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                className={fieldErrors.email ? 'input-error' : ''}
                required
                data-testid="waiver-email"
              />
              {fieldErrors.email && <span className="field-error">{fieldErrors.email}</span>}
            </div>
          </div>
          <div className="form-group">
            <Label htmlFor="phone">Phone *</Label>
            <Input
              id="phone"
              name="phone"
              type="tel"
              placeholder="(000) 000-0000"
              value={formData.phone}
              onChange={handleChange}
              className={fieldErrors.phone ? 'input-error' : ''}
              required
              data-testid="waiver-phone"
            />
            {fieldErrors.phone && <span className="field-error">{fieldErrors.phone}</span>}
          </div>
          <div className="form-divider">
            <span>Address</span>
          </div>
          <div className="form-group">
            <Label htmlFor="streetAddress">Street Address *</Label>
            <Input
              id="streetAddress"
              name="streetAddress"
              placeholder="123 Main Street"
              value={formData.streetAddress}
              onChange={handleChange}
              className={fieldErrors.streetAddress ? 'input-error' : ''}
              required
              data-testid="waiver-street-address"
            />
            {fieldErrors.streetAddress && <span className="field-error">{fieldErrors.streetAddress}</span>}
          </div>
          <div className="form-row">
            <div className="form-group">
              <Label htmlFor="city">City *</Label>
              <Input
                id="city"
                name="city"
                value={formData.city}
                onChange={handleChange}
                className={fieldErrors.city ? 'input-error' : ''}
                required
                data-testid="waiver-city"
              />
              {fieldErrors.city && <span className="field-error">{fieldErrors.city}</span>}
            </div>
            <div className="form-group form-group-small">
              <Label htmlFor="state">State *</Label>
              <Input
                id="state"
                name="state"
                value={formData.state}
                onChange={handleChange}
                maxLength={2}
                placeholder="e.g. NC"
                className={fieldErrors.state ? 'input-error' : ''}
                required
                data-testid="waiver-state"
              />
              {fieldErrors.state && <span className="field-error">{fieldErrors.state}</span>}
            </div>
            <div className="form-group form-group-small">
              <Label htmlFor="zipCode">Zip Code *</Label>
              <Input
                id="zipCode"
                name="zipCode"
                value={formData.zipCode}
                onChange={handleChange}
                maxLength={10}
                placeholder="e.g. 28401"
                className={fieldErrors.zipCode ? 'input-error' : ''}
                required
                data-testid="waiver-zip"
              />
              {fieldErrors.zipCode && <span className="field-error">{fieldErrors.zipCode}</span>}
            </div>
          </div>
          <div className="form-divider">
            <span>Emergency Contact</span>
          </div>
          <div className="form-row">
            <div className="form-group">
              <Label htmlFor="emergencyContactName">Emergency Contact Name *</Label>
              <Input
                id="emergencyContactName"
                name="emergencyContactName"
                value={formData.emergencyContactName}
                onChange={handleChange}
                className={fieldErrors.emergencyContactName ? 'input-error' : ''}
                required
                data-testid="waiver-emergency-name"
              />
              {fieldErrors.emergencyContactName && <span className="field-error">{fieldErrors.emergencyContactName}</span>}
            </div>
            <div className="form-group">
              <Label htmlFor="emergencyContactPhone">Emergency Contact Phone *</Label>
              <Input
                id="emergencyContactPhone"
                name="emergencyContactPhone"
                type="tel"
                placeholder="(000) 000-0000"
                value={formData.emergencyContactPhone}
                onChange={handleChange}
                className={fieldErrors.emergencyContactPhone ? 'input-error' : ''}
                required
                data-testid="waiver-emergency-phone"
              />
              {fieldErrors.emergencyContactPhone && <span className="field-error">{fieldErrors.emergencyContactPhone}</span>}
            </div>
          </div>
        </div>
      ) : (
        <div className="form-section">
          <div className="form-divider first">
            <span>Minor's Information</span>
          </div>
          <div className="form-row">
            <div className="form-group">
              <Label htmlFor="minorFirstName">Minor's First Name *</Label>
              <Input
                id="minorFirstName"
                name="minorFirstName"
                value={formData.minorFirstName}
                onChange={handleChange}
                className={fieldErrors.minorFirstName ? 'input-error' : ''}
                required
                data-testid="waiver-minor-firstname"
              />
              {fieldErrors.minorFirstName && <span className="field-error">{fieldErrors.minorFirstName}</span>}
            </div>
            <div className="form-group">
              <Label htmlFor="minorLastName">Minor's Last Name *</Label>
              <Input
                id="minorLastName"
                name="minorLastName"
                value={formData.minorLastName}
                onChange={handleChange}
                className={fieldErrors.minorLastName ? 'input-error' : ''}
                required
                data-testid="waiver-minor-lastname"
              />
              {fieldErrors.minorLastName && <span className="field-error">{fieldErrors.minorLastName}</span>}
            </div>
          </div>
          <div className="form-group">
            <Label htmlFor="minorDOB">Minor's Date of Birth *</Label>
            <Input
              id="minorDOB"
              name="minorDOB"
              type="date"
              value={formData.minorDOB}
              onChange={handleChange}
              className={fieldErrors.minorDOB ? 'input-error' : ''}
              required
              data-testid="waiver-minor-dob"
            />
            {fieldErrors.minorDOB && <span className="field-error">{fieldErrors.minorDOB}</span>}
          </div>
          <div className="form-divider">
            <span>Parent/Guardian Information</span>
          </div>
          <div className="form-row">
            <div className="form-group">
              <Label htmlFor="parentFirstName">Parent/Guardian First Name *</Label>
              <Input
                id="parentFirstName"
                name="parentFirstName"
                value={formData.parentFirstName}
                onChange={handleChange}
                className={fieldErrors.parentFirstName ? 'input-error' : ''}
                required
                data-testid="waiver-parent-firstname"
              />
              {fieldErrors.parentFirstName && <span className="field-error">{fieldErrors.parentFirstName}</span>}
            </div>
            <div className="form-group">
              <Label htmlFor="parentLastName">Parent/Guardian Last Name *</Label>
              <Input
                id="parentLastName"
                name="parentLastName"
                value={formData.parentLastName}
                onChange={handleChange}
                className={fieldErrors.parentLastName ? 'input-error' : ''}
                required
                data-testid="waiver-parent-lastname"
              />
              {fieldErrors.parentLastName && <span className="field-error">{fieldErrors.parentLastName}</span>}
            </div>
          </div>
          <div className="form-group">
            <Label htmlFor="parentEmail">Parent/Guardian Email *</Label>
            <Input
              id="parentEmail"
              name="parentEmail"
              type="email"
              value={formData.parentEmail}
              onChange={handleChange}
              className={fieldErrors.parentEmail ? 'input-error' : ''}
              required
              data-testid="waiver-parent-email"
            />
            {fieldErrors.parentEmail && <span className="field-error">{fieldErrors.parentEmail}</span>}
          </div>
          <div className="form-group">
            <Label htmlFor="parentPhone">Parent/Guardian Phone *</Label>
            <Input
              id="parentPhone"
              name="parentPhone"
              type="tel"
              placeholder="(000) 000-0000"
              value={formData.parentPhone}
              onChange={handleChange}
              className={fieldErrors.parentPhone ? 'input-error' : ''}
              required
              data-testid="waiver-parent-phone"
            />
            {fieldErrors.parentPhone && <span className="field-error">{fieldErrors.parentPhone}</span>}
          </div>
          <div className="form-divider">
            <span>Parent/Guardian Address</span>
          </div>
          <div className="form-group">
            <Label htmlFor="parentStreetAddress">Street Address *</Label>
            <Input
              id="parentStreetAddress"
              name="parentStreetAddress"
              placeholder="123 Main Street"
              value={formData.parentStreetAddress}
              onChange={handleChange}
              className={fieldErrors.parentStreetAddress ? 'input-error' : ''}
              required
              data-testid="waiver-parent-street-address"
            />
            {fieldErrors.parentStreetAddress && <span className="field-error">{fieldErrors.parentStreetAddress}</span>}
          </div>
          <div className="form-row">
            <div className="form-group">
              <Label htmlFor="parentCity">City *</Label>
              <Input
                id="parentCity"
                name="parentCity"
                value={formData.parentCity}
                onChange={handleChange}
                className={fieldErrors.parentCity ? 'input-error' : ''}
                required
                data-testid="waiver-parent-city"
              />
              {fieldErrors.parentCity && <span className="field-error">{fieldErrors.parentCity}</span>}
            </div>
            <div className="form-group form-group-small">
              <Label htmlFor="parentState">State *</Label>
              <Input
                id="parentState"
                name="parentState"
                value={formData.parentState}
                onChange={handleChange}
                maxLength={2}
                placeholder="e.g. NC"
                className={fieldErrors.parentState ? 'input-error' : ''}
                required
                data-testid="waiver-parent-state"
              />
              {fieldErrors.parentState && <span className="field-error">{fieldErrors.parentState}</span>}
            </div>
            <div className="form-group form-group-small">
              <Label htmlFor="parentZipCode">Zip Code *</Label>
              <Input
                id="parentZipCode"
                name="parentZipCode"
                value={formData.parentZipCode}
                onChange={handleChange}
                maxLength={10}
                placeholder="e.g. 28401"
                className={fieldErrors.parentZipCode ? 'input-error' : ''}
                required
                data-testid="waiver-parent-zip"
              />
              {fieldErrors.parentZipCode && <span className="field-error">{fieldErrors.parentZipCode}</span>}
            </div>
          </div>
        </div>
      )
    },
    // Section 2: Legal Sections 1-2 (with initial after Section 2)
    {
      title: 'Definitions & Assumption of Risk',
      content: (
        <div className="legal-section">
          <div className="legal-header">
            <h3>CAL FIT</h3>
            <h4>ASSUMPTION OF RISK, RELEASE AND WAIVER OF LIABILITY AGREEMENT</h4>
          </div>
          
          <div className="legal-intro">
            <p>
              In consideration for permitting Participant to engage in the physical fitness and recreational
              Activities (as defined hereinbelow) offered by Core Air Life Fitness LLC, d/b/a CAL FIT, the
              undersigned Participant does hereby expressly and irrevocably acknowledge their conscious and
              deliberate decision to partake in such Activities, with full awareness and acceptance of the inherent
              and potential risks associated therewith, and hereby enters into this ASSUMPTION OF RISK,
              RELEASE and WAIVER OF LIABILITY AGREEMENT (this "Agreement"), for themself, their executors,
              administrators, heirs, next of kin, successors, and assigns, expressly assumes all legal
              responsibility for the inherent risks and dangers of the Activities, and acknowledges, understands
              and agrees to be legally bound by the terms and conditions of this Agreement, as follows:
            </p>
          </div>

          <div className="legal-section-block">
            <h5>1. Definitions</h5>
            <p>
              <strong>a. "Participant"</strong> shall refer to the individual whose name is first written hereinabove,
              who is signing this Agreement and will participate in the Activities offered by Provider, as
              herein defined, and in the event of a minor Participant, the parent or guardian signing this
              Agreement on behalf of such minor Participant.
            </p>
            <p>
              <strong>b. "Provider"</strong> shall refer to Core Air Life Fitness LLC, d/b/a CAL FIT, its principals,
              owners, officers, members, agents, contractors, employees, representatives, volunteers,
              successors, assigns and affiliated entities.
            </p>
            <p>
              <strong>c. "Activity" or "Activities"</strong> shall include any and all activities and/or services
              provided, organized, conducted or facilitated and authorized by Provider, including, but not
              limited to, personal and small group fitness consulting, training and coaching; aerial (aerial
              silks, bungee, Lyra hoop and static trapeze) fitness; pole (static, spin and flying) fitness;
              aerial and mat aerobics, pilates and yoga; as well as personal and small group fitness
              excursions and field trips, which may involve the activities previously set forth herein and in
              addition thereto, may additionally involve personal or small group transportation by motor
              vehicle, all-terrain vehicle or watercraft; basketball; soccer; disc golf; pickleball, park play;
              golfing; skating; hiking; running; kayaking; stand up paddleboarding; swimming and other
              aquatic activities to take place in a pool and/or the ocean, and other such indoor and
              outdoor fitness activities and services that may be provided, organized, conducted or
              facilitated and authorized by Provider from time to time in personal or small group settings
              that may involve the use of certain fitness equipment such as bungee harnesses, bungee
              cords, aerial silks, hammocks, trusses, ladders, safety rated rigging straps, carabiners, bow
              shackles, cords, straps, ropes, hooks, clamps, base plates and mounts, among other things.
            </p>
          </div>

          <div className="legal-section-block">
            <h5>2. Participant Acknowledgment and Assumption of Risk</h5>
            <p>
              The Participant, with full knowledge and understanding, hereby unequivocally acknowledges the inherent and potential
              dangers and hazards associated with participating in the Activities offered by Provider, including,
              but not limited to: cuts, scrapes, soft tissue injury, broken bones, concussions, asphyxiation, other
              bodily injury; illness; accident; exposure to changing weather conditions, extreme temperatures
              and/or inclement weather; negligence of others; sunburn, sun stroke, heat exhaustion,
              dehydration, insect/animal bites, brain injury, exposure to and/or infection by communicable
              diseases; drowning, permanent paralysis and/or death.
            </p>
            <p>
              By engaging in any or all of these Activities, the Participant knowingly and willingly assumes complete responsibility for all associated risks,
              both explicit and implied. This includes, but is not limited to, risks emanating from any actions,
              omissions, negligence, or oversight by the Provider; any malfunctions, defects, or inadequacies in
              the equipment or property owned, operated, maintained, controlled or utilized by the Provider in
              providing the Activities; and any other foreseeable or unforeseeable incidents, liabilities, or
              eventualities that may arise during or as a consequence of the Participant's engagement or
              involvement in the Activities.
            </p>
            <p className="legal-emphasis">
              KNOWING THE INHERENT RISKS OF THE ACTIVITIES, PARTICIPANT VOLUNTARILY ASSUME ALL RISKS, RESPONSIBILITIES, AND CONSEQUENCES
              RELATED TO THEIR PARTICIPATION IN THE ACTIVITIES, INCLUDING RISKS STEMMING FROM THE
              NEGLIGENCE OR FAULT OF THE PROVIDER OR OTHERS, EQUIPMENT MALFUNCTIONS, ACTS OF
              NATURE, AND ANY OTHER POSSIBLE SOURCES OF HARM OR INCONVENIENCE.
            </p>
          </div>

          <div className="initial-box mobile-highlight">
            <div className="initial-prompt">
              <span className="initial-arrow">👇</span>
              <Label>Please initial below to confirm you have read and understand Section 2:</Label>
            </div>
            <Input
              className="initials-input"
              placeholder="Your Initials"
              value={initials.section2}
              onChange={(e) => handleInitialChange('section2', e.target.value)}
              maxLength={3}
              data-testid="initials-section2"
            />
            <span className="initial-hint">Enter 2-3 letters (e.g., JS)</span>
          </div>
        </div>
      )
    },
    // Section 3: Health and Safety (with initial)
    {
      title: 'Health and Safety Acknowledgements',
      content: (
        <div className="legal-section">
          <div className="legal-section-block">
            <h5>3. Health and Safety Acknowledgements</h5>
            
            <p>
              <strong>a. Health and Substance Free Assurance.</strong> The Participant acknowledges and
              represents that they are familiar with the scope of physical fitness and recreational
              activities offered by Provider in which Participant is choosing to engage in, and is qualified,
              in good health, and in proper physical condition to engage in such Activities, has no
              medical condition(s) that would prevent safe participation in the Activities and has not
              received any counsel, recommendation, or advice from any medical or healthcare
              professional advising against such participation.
            </p>
            <p>
              Should the Participant's health status change in a manner that might affect safe participation, they are obligated to inform the
              Provider promptly. The Participant additionally confirms that they shall not be intoxicated
              nor under the influence of any narcotics, controlled substances, or medications that might
              impair their faculties or judgment at any time when participating in the Activities.
            </p>
            <p className="legal-emphasis">
              PROVIDER PLACES UTMOST IMPORTANCE ON THE SAFETY OF ALL PARTICIPANTS AND ITS PERSONNEL. AS SUCH, ANY FALSE
              REPRESENTATION OR FAILURE TO DISCLOSE PERTINENT INFORMATION BY THE
              PARTICIPANT MAY RESULT IN LEGAL CONSEQUENCES.
            </p>

            <p>
              <strong>b. Safety.</strong> The Participant additionally acknowledges that they have been advised
              that Participant is required to wear approved safety equipment, as applicable, at all times
              while engaging in such Activities and is required to adhere to all rules applicable
              to Participant's engagement in such Activities, as set forth by Provider, as well as follow
              instructions provided by Provider relating to the Activities.
            </p>
            <p>
              Participant further acknowledges and understands that a violation of rules, instructions and/or requirements
              relating to safety precautions to be followed while engaging in such Activities may result in
              injury to the Participant or to others, and that even when complying with such rules,
              instructions and/or requirements, the Activities necessarily carry certain inherent risks,
              including those risks mentioned herein, as well as other known and unknown risks that
              cannot be eliminated by Provider or any Releasees, regardless of the precautions and care
              taken to avoid such risks.
            </p>

            <p>
              <strong>c. No Reliance.</strong> The Participant understands and agrees that it is their sole
              responsibility to assess the specific risks presented by their engagement in the Activities,
              and further agrees that they are the ultimate judge as to all personal circumstances and
              considerations relating to whether Participant can engage in the Activities offered by
              Providers without risk of harm to themself and/or others.
            </p>
          </div>

          <div className="initial-box mobile-highlight">
            <div className="initial-prompt">
              <span className="initial-arrow">👇</span>
              <Label>Please initial below to confirm you have read and understand Section 3:</Label>
            </div>
            <Input
              className="initials-input"
              placeholder="Your Initials"
              value={initials.section3}
              onChange={(e) => handleInitialChange('section3', e.target.value)}
              maxLength={3}
              data-testid="initials-section3"
            />
            <span className="initial-hint">Enter 2-3 letters (e.g., JS)</span>
          </div>
        </div>
      )
    },
    // Section 4: Release, Waiver, Indemnification (with initial after Section 5)
    {
      title: 'Release of Liability & Indemnification',
      content: (
        <div className="legal-section">
          <div className="legal-section-block">
            <h5>4. Release of Liability and Waiver</h5>
            <p>
              In acknowledgment of the privilege to engage in the Activities, along with the receipt of valuable, adequate, and sufficient benefits and
              considerations, both tangible and intangible, the Participant, acting for and on behalf of
              themselves, as well as their executors, administrators, heirs, next of kin, successors, and assigns,
              hereby irrevocably WAIVES, RELEASES, and DISCHARGES the Provider from any conceivable
              liabilities, claims, actions, damages, costs, or expenses of any nature whatsoever, whether
              foreseen or unforeseen, known or unknown, that may arise out of or relate to the Participant's
              participation in the Activities.
            </p>
            <p>
              This waiver extends perpetually and applies to, but is not limited to,
              liabilities or damages stemming from any act, omission, negligence, fault, or any other cause
              attributable to the Provider or any affiliated entity, including, without restriction, cases involving
              death, disability, personal injury, property damage, property theft, or any other potential harm or
              loss suffered by the Participant.
            </p>
            <p className="legal-emphasis">
              THIS CLAUSE REPRESENTS AN ABSOLUTE WAIVER OF ANY RIGHT TO SEEK LEGAL REMEDIES
              AGAINST PROVIDER AND/OR ITS AFFILIATES. PARTICIPANTS ARE ADVISED TO UNDERSTAND
              AND EVALUATE THE IMPLICATIONS OF THIS RELEASE AND WAIVER COMPREHENSIVELY BEFORE
              SIGNING THIS AGREEMENT OR PROCEEDING WITH THE ACTIVITIES.
            </p>
          </div>

          <div className="legal-section-block">
            <h5>5. Indemnification and Covenant Not to Sue</h5>
            <p>
              The Participant hereby unconditionally and irrevocably agrees to INDEMNIFY, DEFEND, and HOLD HARMLESS Provider against any and all
              losses, liabilities, damages, claims, costs, expenses (including legal fees and expenses), and
              other adverse effects arising directly or indirectly from or in connection with the Participant's
              participation in the Activities. This obligation extends to claims rooted in negligence, strict liability,
              breach of contract, or any other legal theory attributed to the Provider or its affiliates.
            </p>
            <p>
              Furthermore, the Participant explicitly COVENANTS NOT TO INITIATE any form of legal proceedings against the
              Provider or its affiliates related to the Activities, acknowledging the comprehensive protective
              nature of this Agreement.
            </p>
            <p className="legal-emphasis">
              THIS CLAUSE REQUIRES THE PARTICIPANT TO BEAR THE FINANCIAL
              AND LEGAL RESPONSIBILITY FOR CLAIMS OR ISSUES ARISING FROM THE ACTIVITIES, EVEN IF
              THEY RESULT FROM THE PROVIDER'S NEGLIGENCE. PARTICIPANTS ARE ADVISED TO FULLY
              UNDERSTAND AND CONSENT TO THE IMPLICATIONS OF THIS INDEMNITY BEFORE SIGNING THIS
              AGREEMENT OR PROCEEDING WITH THE ACTIVITIES.
            </p>
          </div>

          <div className="initial-box mobile-highlight">
            <div className="initial-prompt">
              <span className="initial-arrow">👇</span>
              <Label>Please initial below to confirm you have read Sections 4-5:</Label>
            </div>
            <Input
              className="initials-input"
              placeholder="Your Initials"
              value={initials.section5}
              onChange={(e) => handleInitialChange('section5', e.target.value)}
              maxLength={3}
              data-testid="initials-section5"
            />
            <span className="initial-hint">Enter 2-3 letters (e.g., JS)</span>
          </div>
        </div>
      )
    },
    // Section 5: Sections 6-10 + NEW Section 11 (Health Requirements)
    {
      title: 'Additional Terms & Health Requirements',
      content: (
        <div className="legal-section">
          <div className="legal-section-block">
            <h5>6. Dispute Resolution and Recovery of Expenses</h5>
            <p>
              The Participant unequivocally acknowledges and agrees that in the event any disagreement or contention arise in connection
              with this Agreement, and should Provider emerge as the prevailing party in such dispute, whether
              through legal proceedings, arbitration, mediation, or any other method of dispute resolution, the
              Participant shall be solely and unreservedly responsible for compensating the Provider for all costs
              and expenses borne by the Provider, including, but not limited to, all reasonable attorney's fees,
              court costs, expert witness fees, and any other related expenses.
            </p>
          </div>

          <div className="legal-section-block">
            <h5>7. Emergency Medical Intervention</h5>
            <p>
              The Participant explicitly acknowledges, agrees, and consents that, in the event of any injury, accident, and/or illness occurring during or subsequent to
              the Participant's engagement in the Activities, the Provider holds the authority and discretion to
              procure, initiate, or allow any required or advisable emergency medical response, treatment, or
              service on the Participant's behalf. This intervention is for the sole purpose of preserving the
              Participant's well-being or life.
            </p>
          </div>

          <div className="legal-section-block">
            <h5>8. Entire Agreement</h5>
            <p>
              This Agreement contains the entire understanding between the Provider and Participant relating to Participant's engagement in the Activities and is binding and
              will remain in effect indefinitely, covering all present and future engagements with Provider unless
              it's otherwise nullified or modified in writing and agreed upon by both parties.
            </p>
          </div>

          <div className="legal-section-block">
            <h5>9. Severability</h5>
            <p>
              The Participant expressly agrees that the foregoing Agreement is intended to be as broad and inclusive as is permitted by the law of the State of North Carolina and that if any
              portion thereof is held invalid, it is agreed that the balance shall continue in full legal force and effect.
            </p>
          </div>

          <div className="legal-section-block">
            <h5>10. Jurisdiction & Governing Law</h5>
            <p>
              This Agreement and any rights and obligations between the parties to this Agreement shall be governed by and interpreted solely in accordance with the laws
              of the State of North Carolina and no other jurisdiction. The Participant and Provider acknowledge
              and agree that any disagreement or dispute between the parties to this Agreement or otherwise
              arising under this Agreement shall be brought solely within the state of North Carolina.
            </p>
          </div>

          <div className="legal-section-block health-requirements">
            <h5>11. Health and Physical Condition Requirements</h5>
            <div className="health-alert">
              <AlertCircle size={24} />
              <span>IMPORTANT: Please read carefully</span>
            </div>
            <p>
              <strong>For Bungee Fitness Activities specifically:</strong> The Participant acknowledges and confirms that they meet the following physical requirements:
            </p>
            <ul className="requirements-list">
              <li><strong>Weight Requirement:</strong> Participant weighs between 90-240 lbs (40.8-108.9 kg)</li>
              <li><strong>No Medical Limitations:</strong> Participant has no medical or physical limitations that would prevent safe participation, including but not limited to:
                <ul>
                  <li>Heart conditions or cardiovascular issues</li>
                  <li>Recent surgeries (within 6 months)</li>
                  <li>Pregnancy</li>
                  <li>Severe joint problems (knees, hips, ankles, shoulders)</li>
                  <li>Back or spinal conditions</li>
                  <li>Neurological conditions</li>
                  <li>High blood pressure (uncontrolled)</li>
                </ul>
              </li>
              <li><strong>Physical Fitness:</strong> Participant is in adequate physical condition to safely engage in high-intensity aerial fitness activities</li>
            </ul>
            <p className="legal-emphasis">
              PROVIDING FALSE INFORMATION REGARDING WEIGHT OR MEDICAL CONDITIONS MAY RESULT IN SERIOUS INJURY. 
              IF YOU ARE UNSURE WHETHER YOU MEET THESE REQUIREMENTS, PLEASE CONSULT WITH YOUR PHYSICIAN BEFORE PARTICIPATING.
            </p>
          </div>

          <div className="initial-box mobile-highlight">
            <div className="initial-prompt">
              <span className="initial-arrow">👇</span>
              <Label>Please initial below to confirm you meet the health requirements:</Label>
            </div>
            <Input
              className="initials-input"
              placeholder="Your Initials"
              value={initials.section11}
              onChange={(e) => handleInitialChange('section11', e.target.value)}
              maxLength={3}
              data-testid="initials-section11"
            />
            <span className="initial-hint">Enter 2-3 letters (e.g., JS)</span>
          </div>
        </div>
      )
    },
    // Section 6: Media Release (Opt-in/Opt-out)
    {
      title: 'Media Release',
      content: (
        <div className="legal-section">
          <div className="legal-section-block media-release">
            <h5>12. Photo & Video Release</h5>
            <div className="media-release-content">
              <p>
                I grant Core Air Life Fitness (GoCalFit) permission to photograph and/or video record 
                {signingFor === 'minor' ? ' my child ' : ' me '} 
                during classes, camps, events, and related activities.
              </p>
              <p>I understand these images and recordings may be used for:</p>
              <ul className="media-uses-list">
                <li>Social media and marketing</li>
                <li>Website content</li>
                <li>Promotional materials</li>
              </ul>
              <p>I waive any right to compensation or approval of final use.</p>
              {signingFor === 'minor' && (
                <p className="minor-note">
                  <strong>For minors:</strong> I am the parent or legal guardian and grant this permission on behalf of my child.
                </p>
              )}
            </div>

            <div className="media-consent-options">
              <p className="consent-question">
                <strong>Do you consent to the media release above?</strong>
              </p>
              <p className="consent-instruction">Please select one option below:</p>
              <div className="consent-buttons">
                <button
                  type="button"
                  className={`consent-option ${mediaConsent === true ? 'selected yes' : ''}`}
                  onClick={() => setMediaConsent(true)}
                  data-testid="media-consent-yes"
                >
                  <CheckCircle2 size={28} />
                  <span className="option-label">Yes, I Consent</span>
                  <span className="option-desc">I grant permission for photos and videos</span>
                </button>
                <button
                  type="button"
                  className={`consent-option ${mediaConsent === false ? 'selected no' : ''}`}
                  onClick={() => setMediaConsent(false)}
                  data-testid="media-consent-no"
                >
                  <AlertCircle size={28} />
                  <span className="option-label">No, I Do Not Consent</span>
                  <span className="option-desc">Please do not photograph or record {signingFor === 'minor' ? 'my child' : 'me'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )
    },
    // Section 7: Final Acknowledgment & Signature
    {
      title: 'Acknowledgment & Signature',
      content: (
        <div className="legal-section">
          <div className="legal-section-block final-acknowledgment">
            <h5>PARTICIPANT AFFIRMATION AND ACKNOWLEDGMENT OF UNDERSTANDING</h5>
            <p>
              THE PARTICIPANT ACKNOWLEDGES, AGREES AND CONFIRMS THAT THEY HAVE READ THE
              ENTIRETY OF THIS AGREEMENT, HAVE HAD AMPLE OPPORTUNITY TO SEEK CLARIFICATION OR
              LEGAL COUNSEL CONCERNING ANY TERMS OR STIPULATIONS THEY MAY NOT FULLY GRASP,
              COMPREHEND THE FULL MAGNITUDE AND IMPLICATIONS OF THIS AGREEMENT, AND
              ACKNOWLEDGE THAT THIS AGREEMENT CONSTITUTES A COMPLETE AND UNCONDITIONAL
              RELEASE OF ALL LIABILITIES AND ACTIONS AGAINST PROVIDER, TO THE BROADEST EXTENT
              ALLOWED BY LAW, AND THAT BY SIGNING THIS BINDING CONTRACTUAL AGREEMENT, THEY ARE
              GIVING UP SUBSTANTIAL RIGHTS, INCLUDING THEIR RIGHT TO SUE THE PROVIDER.
            </p>
            <p>
              PARTICIPANT ADDITIONALLY ACKNOWLEDGES AND UNDERSTANDS THAT THE PROVIDER WILL
              BE PLACING PARAMOUNT RELIANCE ON THIS AGREEMENT IN PERMITTING THE PARTICIPANT'S
              PARTICIPATION IN THE ACTIVITIES, AS THIS AGREEMENT SETS FORTH THE MUTUAL RIGHTS,
              OBLIGATIONS, AND UNDERSTANDINGS BETWEEN THE PARTICIPANT AND THE PROVIDER AND
              WILL REMAIN OPERATIVE FOR ANY AND ALL INTERACTIONS, ENGAGEMENTS, AND SESSIONS
              BETWEEN THE TWO PARTIES, IN PERPETUITY.
            </p>
            <p>
              FURTHERMORE, PARTICIPANT REPRESENTS THAT THEY ARE IN GOOD HEALTH AND PROPER
              PHYSICAL CONDITION TO ENGAGE IN SUCH ACTIVITIES AND THAT PARTICIPANT IS CAPABLE OF
              ENGAGING IN SUCH ACTIVITIES SAFELY AND IN ACCORDANCE WITH ANY/ALL INSTRUCTIONS,
              RULES AND/OR REQUIREMENTS APPLICABLE THERETO.
            </p>
            <p className="legal-emphasis">
              BY AFFIXING THEIR SIGNATURE BELOW, PARTICIPANT UNEQUIVOCALLY ATTESTS THAT THEY
              ARE PROCEEDING WITHOUT ANY EXTERNAL COERCION, PRESSURE OR UNDUE INFLUENCE
              AND ARE SIGNING THIS AGREEMENT ENTIRELY OUT OF THEIR OWN VOLITION AND INFORMED
              JUDGMENT, SIGNIFYING THEIR UNCONDITIONAL ACCEPTANCE OF THESE TERMS.
            </p>
          </div>

          {/* Signer confirmation for minor waivers */}
          {signingFor === 'minor' && (
            <div className="signer-confirmation-section">
              <h5>Signer Confirmation</h5>
              <p className="signer-question">
                Are you <strong>{formData.parentFirstName} {formData.parentLastName}</strong>?
              </p>
              <div className="signer-options">
                <button
                  type="button"
                  className={`signer-option ${signerIsParent === true ? 'selected yes' : ''}`}
                  onClick={() => {
                    setSignerIsParent(true);
                    setSignerInfo({ signerName: '', signerRelationship: '' });
                  }}
                  data-testid="signer-is-parent-yes"
                >
                  <CheckCircle2 size={24} />
                  <span>Yes, that's me</span>
                </button>
                <button
                  type="button"
                  className={`signer-option ${signerIsParent === false ? 'selected no' : ''}`}
                  onClick={() => setSignerIsParent(false)}
                  data-testid="signer-is-parent-no"
                >
                  <User size={24} />
                  <span>No, I'm signing on their behalf</span>
                </button>
              </div>
              
              {signerIsParent === false && (
                <div className="alternate-signer-fields">
                  <div className="form-group">
                    <Label htmlFor="signerName">Your Full Name *</Label>
                    <Input
                      id="signerName"
                      name="signerName"
                      value={signerInfo.signerName}
                      onChange={(e) => setSignerInfo(prev => ({ ...prev, signerName: e.target.value }))}
                      placeholder="Enter your full name"
                      required
                      data-testid="signer-name"
                    />
                  </div>
                  <div className="form-group">
                    <Label htmlFor="signerRelationship">Relationship to Child *</Label>
                    <select
                      id="signerRelationship"
                      name="signerRelationship"
                      value={signerInfo.signerRelationship}
                      onChange={(e) => setSignerInfo(prev => ({ ...prev, signerRelationship: e.target.value }))}
                      className="signer-relationship-select"
                      required
                      data-testid="signer-relationship"
                    >
                      <option value="">Select relationship...</option>
                      <option value="Grandparent">Grandparent</option>
                      <option value="Step-Parent">Step-Parent</option>
                      <option value="Legal Guardian">Legal Guardian</option>
                      <option value="Aunt/Uncle">Aunt/Uncle</option>
                      <option value="Other Authorized Adult">Other Authorized Adult</option>
                    </select>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="signature-section">
            <h5>{signingFor === 'minor' ? 'Parent/Guardian Signature' : 'Participant Signature'}</h5>
            <p className="signature-instruction">
              Please sign in the box below using your finger or mouse:
            </p>
            <div className="signature-pad-container">
              <SignatureCanvas
                ref={sigPadRef}
                canvasProps={{
                  className: 'signature-canvas',
                  'data-testid': 'signature-pad'
                }}
                backgroundColor="white"
              />
            </div>
            <div className="signature-actions">
              <Button 
                type="button" 
                variant="outline" 
                onClick={clearSignature}
                className="clear-signature-btn"
              >
                Clear Signature
              </Button>
            </div>
            <p className="signature-date">
              Date: {new Date().toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
          </div>
        </div>
      )
    }
  ];
};
